import React, {
  useMemo,
  useState,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import {
  formatPlayerTime,
  formatSplitterTime,
  scalledHeight,
} from '@wajeez-web/core/utils';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Chapter } from './AudioSegmentManager';
import { AddChapterModal } from './AddChapterModal';
import ChapterSegment from './ChapterSegment';

interface ProcessedChaptersResult {
  chapters: Chapter[];
  fullText: string;
}

interface AudioContentSplitterProps {
  currentTime?: number;
  setCurrentTime: (time: number) => void;
  duration: number;
  chapters: Chapter[];
  setChapters: React.Dispatch<React.SetStateAction<Chapter[]>>;
  isPlayerTimeChange: boolean;
  originalFullText: string;
  isRtl: boolean;
  setFullText: React.Dispatch<React.SetStateAction<string>>;
}

const AudioContentSplitter = ({
  currentTime = 0,
  setCurrentTime,
  duration,
  chapters,
  setChapters,
  isPlayerTimeChange,
  originalFullText,
  isRtl,
  setFullText,
}: AudioContentSplitterProps) => {
  const [openModalId, setOpenModalId] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [isSegmentDragging, setIsSegmentDragging] = useState<boolean>(false);
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const thumbRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();

  const containerHeight = scalledHeight(duration);

  const sortedChapters = useMemo(
    () => [...chapters].sort((a, b) => a.point - b.point),
    [chapters]
  );

  const calculateTimeFromPosition = useCallback(
    (clientY: number, containerRect: DOMRect) => {
      const containerHeight = containerRect.height;
      const relativeY = Math.max(
        0,
        Math.min(clientY - containerRect.top, containerHeight)
      );
      const percentage = relativeY / containerHeight;
      return Math.min(duration * percentage, duration);
    },
    [duration]
  );

  const handleThumbDrag = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();

      setIsDragging(true);
      const container = e.currentTarget.parentElement;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const newTime = calculateTimeFromPosition(e.clientY, rect);
      setCurrentTime(newTime);

      const handleMouseMove = (e: MouseEvent) => {
        const newTime = calculateTimeFromPosition(e.clientY, rect);
        setCurrentTime(newTime);
      };

      const handleMouseUp = () => {
        setIsDragging(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [calculateTimeFromPosition, setCurrentTime]
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (isSegmentDragging) return;

      const target = e.target as HTMLElement;
      if (target.closest('.modal') || target.closest('.add-button')) {
        return;
      }

      setIsDragging(true);
      const container = e.currentTarget;
      const rect = container.getBoundingClientRect();
      const newTime = calculateTimeFromPosition(e.clientY, rect);
      setCurrentTime(newTime);

      const handleMouseMove = (e: MouseEvent) => {
        const newTime = calculateTimeFromPosition(e.clientY, rect);
        setCurrentTime(newTime);
      };

      const handleMouseUp = () => {
        setIsDragging(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [calculateTimeFromPosition, setCurrentTime]
  );

  const handleTrackClick = useCallback(
    (e: React.MouseEvent) => {
      if (isSegmentDragging) return;

      const target = e.target as HTMLElement;
      if (target.closest('.modal') || target.closest('.add-button')) {
        return;
      }

      const container = e.currentTarget;
      const rect = container.getBoundingClientRect();
      const newTime = calculateTimeFromPosition(e.clientY, rect);
      setCurrentTime(newTime);
    },
    [calculateTimeFromPosition, setCurrentTime]
  );

  // const isChapterExistsAtCurrentTime = useMemo(() => {
  //   const currentTimePercentage = (currentTime / duration) * 100;
  //   return sortedChapters.some(
  //     (chapter) => Math.abs(chapter.point - currentTimePercentage) < 0.1
  //   );
  // }, [currentTime, duration, sortedChapters]);

  const audioPointPercent = (point: number) => {
    return point / 100;
  };

  function processChapters(
    newChapter: Chapter,
    chapters: Chapter[]
  ): ProcessedChaptersResult {
    let currentIndex = 0;
    let processedChapters: Chapter[] = [];

    if (!chapters || chapters.length === 0) {
      processedChapters = [
        {
          ...newChapter,
          fromIndex: 0,
          toIndex: newChapter.text.length,
          audioChapters: [
            {
              narratorId: 1,
              from: 0,
              to: audioPointPercent(newChapter.point),
            },
          ],
        },
      ];

      return {
        chapters: processedChapters,
        fullText: newChapter.text,
      };
    }

    const allChapters = [...chapters, newChapter];
    const sortedChapters = [...allChapters].sort((a, b) => a.point - b.point);

    processedChapters = sortedChapters.map((chapter, index, arr) => {
      const fromIndex = currentIndex;
      const toIndex = currentIndex + chapter.text.length;

      const previousPoint = index === 0 ? 0 : arr[index - 1].point;

      currentIndex = toIndex;

      return {
        ...chapter,
        fromIndex,
        toIndex,
        audioChapters: [
          {
            narratorId: 1,
            from: audioPointPercent(previousPoint),
            to: audioPointPercent(chapter.point),
          },
        ],
      };
    });

    const fullText = processedChapters.map((chapter) => chapter.text).join('');

    return {
      chapters: processedChapters,
      fullText,
    };
  }

  function updateAndReindexChaptersById(
    chapters: Chapter[],
    updatedChapter: Chapter
  ): ProcessedChaptersResult {
    const updatedChapters = chapters.map((chapter) =>
      chapter.id === updatedChapter.id ? updatedChapter : chapter
    );

    const sortedChapters = [...updatedChapters].sort(
      (a, b) => a.point - b.point
    );

    let currentIndex = 0;
    const processedChapters = sortedChapters.map((chapter, index, arr) => {
      const fromIndex = currentIndex;
      const toIndex = currentIndex + chapter.text.length;

      const previousPoint = index === 0 ? 0 : arr[index - 1].point;
      const audioPointPercent = (point: number) => point / 100;

      currentIndex = toIndex;

      return {
        ...chapter,
        fromIndex,
        toIndex,
        audioChapters: [
          {
            narratorId: 1,
            from: audioPointPercent(previousPoint),
            to: audioPointPercent(chapter.point),
          },
        ],
      };
    });

    const fullText = processedChapters.map((chapter) => chapter.text).join('');

    return {
      chapters: processedChapters,
      fullText,
    };
  }

  const handleAddChapter = (newChapter: Chapter) => {
    const { chapters: modifiedChapters, fullText } = processChapters(
      newChapter,
      chapters
    );
    setChapters(modifiedChapters);
    setFullText(fullText);
    setShowAddModal(false);
  };

  const handleEditChapter = (updatedChapter: Chapter) => {
    const { chapters: modifiedChapters, fullText } =
      updateAndReindexChaptersById(chapters, updatedChapter);
    setChapters(modifiedChapters);
    setFullText(fullText);
    setOpenModalId(null);
  };

  const handleDeleteChapter = (id: number | undefined) => {
    setChapters((prev) => prev.filter((ch) => ch.id !== id));
  };

  const handleOpenEditModal = (id: number) => {
    setOpenModalId((prevId) => (prevId === id ? null : id));
  };

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent | KeyboardEvent) => {
      const SMALL_STEP = 1; // 1 second
      const LARGE_STEP = 5; // 5 seconds

      let newTime = currentTime;

      switch (e.key) {
        case 'ArrowUp':
        case 'ArrowLeft':
          e.preventDefault();
          newTime = Math.max(
            0,
            currentTime - (e.shiftKey ? LARGE_STEP : SMALL_STEP)
          );
          break;
        case 'ArrowDown':
        case 'ArrowRight':
          e.preventDefault();
          newTime = Math.min(
            duration,
            currentTime + (e.shiftKey ? LARGE_STEP : SMALL_STEP)
          );
          break;
        case 'Home':
          e.preventDefault();
          newTime = 0;
          break;
        case 'End':
          e.preventDefault();
          newTime = duration;
          break;
        case 'PageUp':
          e.preventDefault();
          newTime = Math.max(0, currentTime - 10);
          break;
        case 'PageDown':
          e.preventDefault();
          newTime = Math.min(duration, currentTime + 10);
          break;
        default:
          return;
      }

      setCurrentTime(newTime);
    },
    [currentTime, duration, setCurrentTime]
  );

  useEffect(() => {
    thumbRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [isPlayerTimeChange]);

  useEffect(() => {
    if (originalFullText) {
      setFullText(originalFullText);
    }
  }, [originalFullText]);

  return (
    <div className="w-full">
      {containerHeight ? (
        <div className="overflow-y-auto h-[700px]">
          <div
            className="w-full relative ps-24"
            style={{ height: `${containerHeight}px` }}
            onMouseDown={handleMouseDown}
            onClick={(e) => {
              thumbRef.current?.focus();
              handleTrackClick(e);
            }}
          >
            <div
              className="bg-blue-200 w-2 rounded-full relative h-full cursor-pointer"
              // onMouseDown={handleMouseDown}
              // onClick={handleTrackClick}
            >
              {/* Draggable Thumb */}
              <div
                ref={thumbRef}
                className={`w-5 h-5 absolute bg-blue-400 rounded-full right-[-6px] z-40 cursor-pointer`}
                style={{
                  top: `${(currentTime / duration) * 100}%`,
                }}
                role="slider"
                tabIndex={0}
                aria-label="Audio timeline"
                aria-valuemin={0}
                aria-valuemax={duration}
                aria-valuenow={currentTime}
                aria-valuetext={formatSplitterTime(currentTime, duration)}
                onMouseDown={handleThumbDrag}
                onKeyDown={handleKeyDown}
              />
            </div>

            <div
              className="absolute right-[25px] add-button bg-blue-400 p-2 rounded-lg text-[12px] font-bold text-white z-50"
              style={{
                top: `${(currentTime / duration) * 100}%`,
              }}
            >
              {formatPlayerTime(currentTime)}
            </div>

            {/* Add Chapter Button */}
            {currentTime !== 0 && (
              <button
                className="add-button bg-blue-400 p-2 rounded-lg text-[12px] font-bold text-white absolute mr-5 z-50"
                style={{
                  top: `${(currentTime / duration) * 100}%`,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowAddModal(!showAddModal);
                }}
              >
                <Plus size={16} />
              </button>
            )}

            {/* Add Chapter Modal */}
            {setShowAddModal && (
              <AddChapterModal
                currentTime={currentTime}
                duration={duration}
                onAdd={handleAddChapter}
                onClose={() => setShowAddModal(false)}
                isOpen={showAddModal}
                isRtl={isRtl}
              />
            )}

            {/* Chapter Segments */}
            {sortedChapters.map((chapter, index) => (
              <ChapterSegment
                key={chapter.point}
                chapter={chapter}
                setChapters={setChapters}
                duration={duration}
                sortedChapters={sortedChapters}
                index={index}
                openModalId={openModalId}
                handleOpenModal={handleOpenEditModal}
                isRtl={isRtl}
                handleUpdateChapter={handleEditChapter}
                setIsDragging={setIsSegmentDragging}
                onDeleteChapter={handleDeleteChapter}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="flex justify-center items-center">
          {t(`general.Loading`)}
        </div>
      )}
    </div>
  );
};

export default AudioContentSplitter;
